import {
  Badge,
  Button,
  Checkbox,
  DatePicker,
  type FilterOption,
  Icon,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
} from "@assetwatch/ui";
import { useId, useState } from "react";

export type ListFilterFacet = {
  key: string;
  label: string;
  options: FilterOption[];
  value: string[];
  onChange: (values: string[]) => void;
  selectionMode?: "single" | "multiple";
  searchable?: boolean;
};

export type ListDateFacet = {
  key: string;
  label: string;
  from: Date | null;
  to: Date | null;
  onFromChange: (date: Date | null) => void;
  onToChange: (date: Date | null) => void;
};

export type ListFacet = ListFilterFacet | ListDateFacet;

type ListFiltersProps = {
  ariaLabel: string;
  facets: ListFacet[];
  onReset: () => void;
  activeFilterCount: number;
  /**
   * Whether Clear Filters has anything to clear. Defaults to whether any
   * facet filter is active; a screen whose Clear also resets its search or
   * view passes its own value.
   */
  canReset?: boolean;
};

function isOptionFacet(facet: ListFacet): facet is ListFilterFacet {
  return "options" in facet;
}

function FilterRow({
  facet,
  children,
}: {
  facet: ListFacet;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const labelId = useId();
  const summaryId = useId();
  const selectedCount = isOptionFacet(facet) ? facet.value.length : 0;
  const isSet = isOptionFacet(facet)
    ? selectedCount > 0
    : Boolean(facet.from || facet.to);

  return (
    <div className="border-b border-outline-subtle">
      <button
        aria-describedby={selectedCount > 0 ? summaryId : undefined}
        aria-expanded={open}
        aria-labelledby={labelId}
        className="flex h-(--spacing-40) w-full items-center gap-(--spacing-8) px-(--spacing-16) text-left text-text-default transition-colors hover:bg-surface-container-low"
        onClick={() => setOpen(!open)}
        type="button"
      >
        <span className="min-w-0 flex-1" data-slot="filter-row-label">
          <Label
            id={labelId}
            size="lg"
            truncate
            weight={isSet ? "semibold" : "medium"}
          >
            {facet.label}
          </Label>
        </span>
        {selectedCount > 0 ? (
          /* @ds-deviation: Label has no brand tone for the selected-count
             summary shown in the Requests reference. */
          <span
            className="shrink-0 whitespace-nowrap text-primary tabular-nums"
            data-slot="filter-row-summary"
            id={summaryId}
          >
            <Label size="sm" tone="inherit">{`One of ${selectedCount}`}</Label>
          </span>
        ) : null}
        <span className={open ? "inline-block rotate-180" : "inline-block"}>
          <Icon name="chevron-down" size="xs" tone="secondary" />
        </span>
      </button>
      {open ? (
        <div className="px-(--spacing-16) pt-(--spacing-4) pb-(--spacing-12)">
          {children}
        </div>
      ) : null}
    </div>
  );
}

/* @ds-deviation: the Requests reference uses an inline checklist. The shared
   MultiSelect opens another popover, so this composes Checkbox and Input. */
function OptionList({ facet }: { facet: ListFilterFacet }) {
  const [optionQuery, setOptionQuery] = useState("");
  const normalizedQuery = optionQuery.trim().toLowerCase();
  const visibleOptions = normalizedQuery
    ? facet.options.filter((option) =>
        option.label.toLowerCase().includes(normalizedQuery),
      )
    : facet.options;

  function toggle(value: string, checked: boolean) {
    facet.onChange(
      checked
        ? facet.selectionMode === "single"
          ? [value]
          : [...facet.value, value]
        : facet.value.filter((selected) => selected !== value),
    );
  }

  return (
    <div className="grid gap-(--spacing-8)">
      {(facet.searchable ?? facet.options.length > 6) ? (
        <Input
          aria-label={`Search ${facet.label.toLowerCase()}`}
          leadingIcon="search"
          onChange={(event) => setOptionQuery(event.target.value)}
          placeholder={`Search ${facet.label.toLowerCase()}`}
          reserveSubtextSpace={false}
          size="sm"
          value={optionQuery}
        />
      ) : null}
      {visibleOptions.length > 0 ? (
        <ul
          aria-label={`${facet.label} options`}
          className="grid gap-(--spacing-8)"
        >
          {visibleOptions.map((option) => (
            <li key={option.value}>
              <Checkbox
                checked={facet.value.includes(option.value)}
                label={option.label}
                onCheckedChange={(checked) => toggle(option.value, checked)}
              />
            </li>
          ))}
        </ul>
      ) : (
        <Text size="sm" tone="subtle">
          {facet.options.length > 0 ? "No matches" : "No options available"}
        </Text>
      )}
    </div>
  );
}

export function ListFilters({
  ariaLabel,
  facets,
  onReset,
  activeFilterCount,
  canReset = activeFilterCount > 0,
}: ListFiltersProps) {
  const [open, setOpen] = useState(false);
  const [filterQuery, setFilterQuery] = useState("");
  const normalizedQuery = filterQuery.trim().toLowerCase();
  const visibleFacets = facets
    .map((facet): ListFacet | null => {
      if (
        !normalizedQuery ||
        facet.label.toLowerCase().includes(normalizedQuery)
      )
        return facet;
      if (!isOptionFacet(facet)) return null;
      const options = facet.options.filter((option) =>
        option.label.toLowerCase().includes(normalizedQuery),
      );
      return options.length
        ? { ...facet, options, searchable: facet.options.length > 6 }
        : null;
    })
    .filter((facet): facet is ListFacet => facet !== null);

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen);
    if (!nextOpen) setFilterQuery("");
  }

  return (
    <Popover onOpenChange={handleOpenChange} open={open}>
      <PopoverTrigger
        render={
          <button
            aria-expanded={open}
            aria-haspopup="dialog"
            className={`inline-flex h-(--spacing-32) items-center gap-(--spacing-8) rounded-(--radius-sm) border px-(--spacing-12) font-(family-name:--font-family-label) text-(length:--font-size-body-medium) font-(number:--font-weight-medium) transition-colors ${
              activeFilterCount > 0
                ? /* @ds-deviation: the POC active trigger uses primary-60
                    border, primary-99 fill and primary-40 text; production has
                    no exact tokens, so this approximates with primary and an
                    alpha tint — promote an active filter-trigger variant */
                  "border-primary bg-primary/8 text-primary hover:bg-primary/12"
                : "border-button-outline-default-border bg-button-outline-default-background text-button-outline-default-text hover:bg-button-outline-hover-background"
            }`}
            data-active={activeFilterCount > 0 ? "true" : undefined}
            type="button"
          >
            <Icon name="bars-filter-regular" size="xs" tone="inherit" />
            <span>Filters</span>{" "}
            {activeFilterCount > 0 ? (
              <Badge
                aria-label={`${activeFilterCount} active`}
                count={activeFilterCount}
                tone="activity"
              />
            ) : null}
            <span className={open ? "inline-block rotate-180" : "inline-block"}>
              <Icon name="chevron-down" size="xs" tone="inherit" />
            </span>
          </button>
        }
      />
      <PopoverContent align="start" side="bottom" sideOffset={4}>
        <div
          aria-label={ariaLabel}
          className="flex max-h-[544px] w-[300px] max-w-[calc(100vw-var(--spacing-32))] flex-col overflow-hidden rounded-(--radius-lg) border border-outline-subtle bg-surface shadow-elevation-3"
          role="dialog"
        >
          <div className="shrink-0 border-b border-outline-subtle px-(--spacing-12) py-(--spacing-12)">
            <Input
              aria-label="Search filters"
              leadingIcon="search"
              onChange={(event) => setFilterQuery(event.target.value)}
              placeholder="Search filters"
              reserveSubtextSpace={false}
              size="md"
              value={filterQuery}
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {visibleFacets.map((facet) => (
              <FilterRow facet={facet} key={facet.key}>
                {isOptionFacet(facet) ? (
                  <OptionList facet={facet} />
                ) : (
                  <div className="grid gap-(--spacing-12)">
                    <DatePicker
                      label="From"
                      onValueChange={facet.onFromChange}
                      size="sm"
                      value={facet.from}
                    />
                    <DatePicker
                      label="To"
                      onValueChange={facet.onToChange}
                      size="sm"
                      value={facet.to}
                    />
                  </div>
                )}
              </FilterRow>
            ))}
            {visibleFacets.length === 0 ? (
              <div className="px-(--spacing-16) py-(--spacing-16)">
                <Text size="sm" tone="subtle">
                  No matching filters
                </Text>
              </div>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center justify-end border-t border-outline-subtle px-(--spacing-16) py-(--spacing-12)">
            {/* @ds-deviation: the POC footer button reads "Reset all"; product
                chose "Clear Filters" — fix in POC */}
            <Button
              disabled={!canReset}
              onClick={onReset}
              size="sm"
              variant="ghost"
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
